// SustainabilityImpactPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, Grid, List, ListItem, ListItemText } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const SustainabilityImpactPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Sustainability', path: '/sustainability' },
    { name: 'Sustainability & Impact', path: '/sustainability' },
  ];

  const impactAreas = [
    {
      title: 'Environmental Impact',
      description: 'We significantly reduce landfill waste and conserve natural resources by promoting the recycling of various materials. Our platform facilitates the diversion of tons of scrap from landfills annually.',
      metrics: ['Tons of CO2 emissions saved', 'Gallons of water conserved', 'Energy savings in kWh'],
    },
    {
      title: 'Economic Impact',
      description: 'By creating efficient markets for scrap materials, we support local economies, generate new business opportunities, and contribute to the growth of the recycling industry.',
      metrics: ['New jobs created', 'Value generated in scrap trade', 'Support for small and medium enterprises'],
    },
    {
      title: 'Social Impact',
      description: 'Our commitment extends to fostering safe and ethical trading practices, ensuring fair deals, and promoting responsible resource management across the supply chain.',
      metrics: ['Community engagement programs', 'Ethical sourcing compliance', 'Worker safety initiatives'],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Sustainability & Impact
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Our platform is built on a strong foundation of sustainability. We are committed to fostering a circular economy by maximizing resource recovery and minimizing environmental footprint. Discover the positive impact we create.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {impactAreas.map((area, index) => (
            <Box
              key={index}
              sx={{
                mb: 6,
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                background: 'background.paper',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: 'primary.dark' }}>
                {area.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                {area.description}
              </Typography>

              <Grid container spacing={4}>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ color: 'secondary.main', mb: 1.5 }}>
                      Key Metrics & Focus:
                    </Typography>
                    <List dense disablePadding>
                      {area.metrics.map((metric, metricIndex) => (
                        <ListItem key={metricIndex} disablePadding sx={{ py: 0.5 }}>
                          <ListItemText primary={metric} primaryTypographyProps={{ variant: 'body1', color: 'text.secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default SustainabilityImpactPage;
